// Wave 20.40 — Session detail dispatcher.
//
// Everything is a "session" now — indoor aero sessions and outdoor
// rides/runs both live under /sessions/:id. This component fetches
// the minimal `type` field for the given session and picks which
// detail view to render:
//
//   indoor_aero                        → <SessionDetail />  (aero review)
//   outdoor_cycling | outdoor_running  → <ActivityDetail /> (ride/run)
//
// Both leaf components read the id via useParams so they render
// unchanged inside this wrapper.

import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import PageLayout from './components/PageLayout';
import SessionDetail from './SessionDetail';
import ActivityDetail from './ActivityDetail';
import { colors } from './theme';

const API = process.env.REACT_APP_API || '';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || '';

type SessionType = 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running' | 'unknown';

export default function SessionRouter() {
  const { id } = useParams<{ id: string }>();
  const [type, setType] = useState<SessionType | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    let cancelled = false;
    fetch(`${API}/sessions/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          return null;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled || !data) return;
        setType((data.type as SessionType) ?? 'unknown');
      })
      .catch(() => {
        // Fall through to indoor view; SessionDetail will show its
        // own error state.
        setType('indoor_aero');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (notFound) return <Navigate to="/feed" replace />;
  if (!type) {
    return (
      <PageLayout maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240,
          }}
        >
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }
  if (type === 'outdoor_cycling' || type === 'outdoor_running') {
    return <ActivityDetail />;
  }
  return <SessionDetail />;
}
